import React, { useState, useRef } from 'react';
import { Box, IconButton, CircularProgress, Typography } from '@mui/material';
import { Sparkles, Upload, Trash2 } from 'lucide-react';
import {
  getMediaUrl,
  getConventionalFilename,
  getTimestampField,
  type MediaEntityType,
  logger,
} from '@musedrops/shared';
import {
  supabase,
  getEdgeFunctionAuthHeaders,
} from '../../../services/supabase';

// Still needed for raw fetch calls to Edge Functions below.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

interface ImageFieldProps {
  /** Display label for the field */
  label: string;
  /** Current filename (not full path) */
  value: string | null;
  /** Callback when file is uploaded - receives filename only */
  // eslint-disable-next-line no-unused-vars
  onUpload?: (filename: string) => void;
  /** Callback when file is deleted */
  onDelete?: () => void;
  /** Callback when timestamp changes (for immediate cache-busting) */
  // eslint-disable-next-line no-unused-vars
  onTimestampChanged?: (timestamp: string) => void;
  /** Callback for AI generation */
  onGenerate?: () => void;
  /** Storage folder type */
  storageFolder: 'shows' | 'stories';
  /** Entity slug for constructing storage path */
  entitySlug?: string;
  /** Entity ID for database updates */
  entityId?: string;
  /** Timestamp for cache-busting */
  imageUpdatedAt?: string | null;
  /** Accepted file types */
  accept?: string;
  /** Disable all interactions */
  disabled?: boolean;
  /** Show upload button */
  showUpload?: boolean;
  /** Show generate button */
  showGenerate?: boolean;
  /** Upload button tooltip */
  uploadTitle?: string;
  /** Generate button tooltip */
  generateTitle?: string;
  /** Is generation in progress */
  isGenerating?: boolean;
  /** Helper text to display */
  helperText?: string;
  /** Image display size */
  size?: number;
}

/**
 * Shared Image Field Component
 *
 * Provides upload, generation, playback, and delete functionality for images with:
 * - Overlaid icon buttons for upload/generate (when no image)
 * - Overlaid delete button (when image exists)
 * - Click to open full size in new tab
 * - Timestamp-based cache-busting
 * - Error handling and loading states
 *
 * Refactored to match AudioFileField pattern with overlaid controls
 */
export const ImageField: React.FC<ImageFieldProps> = ({
  label,
  value,
  onUpload,
  onDelete,
  onTimestampChanged,
  onGenerate,
  storageFolder,
  entitySlug,
  entityId,
  imageUpdatedAt,
  accept = '.jpg,.jpeg,.png',
  disabled = false,
  showUpload = true,
  showGenerate = false,
  uploadTitle = 'Upload image',
  generateTitle = 'Generate image using AI',
  isGenerating = false,
  helperText,
  size = 200,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Construct image URL with timestamp for cache-busting
  const imageUrl = React.useMemo(() => {
    if (!value || !entitySlug) return undefined;
    if (imageUpdatedAt) {
      return `${getMediaUrl(storageFolder, entitySlug, value)}?t=${new Date(imageUpdatedAt).getTime()}`;
    }
    return getMediaUrl(storageFolder, entitySlug, value);
  }, [value, entitySlug, storageFolder, imageUpdatedAt]);

  const handleUploadClick = () => {
    if (!entitySlug) {
      setError('Cannot upload: entity must be saved first to generate slug');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      setError('Please select a JPG or PNG image file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Use conventional filename
      const entityType: MediaEntityType =
        storageFolder === 'shows' ? 'show' : 'story';
      const filename = getConventionalFilename(entityType, 'coverImage');
      const storagePath = `${storageFolder}/${entitySlug}/${filename}`;

      // Upload to Supabase Storage (upsert=true to overwrite existing)
      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Update only timestamp (files use conventional names)
      if (entityId) {
        const timestampField = getTimestampField(entityType, 'coverImage');

        const { error: updateError } = await supabase
          .from(storageFolder)
          .update({
            [timestampField]: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', entityId);

        if (updateError) {
          logger.error('Failed to update timestamp:', updateError);
          throw new Error(`Failed to update timestamp: ${updateError.message}`);
        }
      }

      // Cleanup old image format variations after upload via edge function
      logger.info(
        `[Upload] Starting cleanup for ${entityType}/${entitySlug}/coverImage`
      );
      try {
        const cleanupResponse = await fetch(
          `${supabaseUrl}/functions/v1/cleanup-media-variants`,
          {
            method: 'POST',
            headers: await getEdgeFunctionAuthHeaders(),
            body: JSON.stringify({
              entityType,
              entitySlug,
              mediaType: 'coverImage',
            }),
          }
        );

        const cleanupResult = await cleanupResponse.json();
        logger.info(`[Upload] Cleanup result:`, cleanupResult);

        if (!cleanupResult.success) {
          logger.warn('Image cleanup failed:', cleanupResult.error);
          // Don't throw - cleanup failure shouldn't fail the upload
        } else if (cleanupResult.deletedCount > 0) {
          logger.info(
            `Cleaned up ${cleanupResult.deletedCount} old image variations`
          );
        } else {
          logger.info('No old image variations found to clean up');
        }
      } catch (cleanupError) {
        logger.warn('Cleanup edge function call failed:', cleanupError);
        // Don't throw - cleanup failure shouldn't fail the upload
      }

      // Notify parent components
      onUpload?.(filename);

      // Update timestamp immediately for cache-busting
      onTimestampChanged?.(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!value || !entitySlug || !entityId || !onDelete) return;

    setDeleting(true);
    setError(null);

    try {
      // Delete file from storage
      const filePath = `${storageFolder}/${entitySlug}/${value}`;
      const { error: deleteError } = await supabase.storage
        .from('media')
        .remove([filePath]);

      if (deleteError) {
        logger.error('Failed to delete file from storage:', deleteError);
      }

      // Clear timestamp field (files use conventional names)
      const entityType: MediaEntityType =
        storageFolder === 'shows' ? 'show' : 'story';
      const timestampField = getTimestampField(entityType, 'coverImage');

      const { error: updateError } = await supabase
        .from(storageFolder)
        .update({
          [timestampField]: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', entityId);

      if (updateError) {
        logger.error('Failed to clear timestamp:', updateError);
        setError('Failed to delete file');
        return;
      }

      onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleImageClick = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };
  const canUpload =
    showUpload && entitySlug && !uploading && !disabled && onUpload;
  const canGenerate = showGenerate && onGenerate;
  const canDelete = value && entitySlug && entityId && onDelete && !deleting;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Label */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>

      {/* Horizontal Layout: Image + Buttons Column */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        {/* Image Container */}
        <Box
          sx={{
            position: 'relative',
            width: size,
            height: size,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {/* Image or Loading State or Empty State */}
          {(() => {
            const isLoading = uploading || isGenerating;
            const hasImage = imageUrl && value && !isLoading;

            if (hasImage) {
              return (
                <Box
                  onClick={handleImageClick}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleImageClick();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                  }}
                  title="Click to open full size in new tab"
                >
                  <img
                    key={imageUrl}
                    src={imageUrl}
                    alt={label}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
              );
            }

            if (isLoading) {
              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    {uploading ? 'Uploading...' : 'Generating...'}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{ fontStyle: 'italic' }}
                >
                  No image
                </Typography>
              </Box>
            );
          })()}
        </Box>

        {/* Buttons Column (to the right of image) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {/* Generate Icon (always visible when enabled) */}
          {canGenerate && (
            <IconButton
              onClick={onGenerate}
              disabled={isGenerating || disabled || uploading}
              title={generateTitle}
              size="small"
            >
              {isGenerating ? (
                <CircularProgress size={20} />
              ) : (
                <Sparkles size={20} />
              )}
            </IconButton>
          )}

          {/* Upload Icon (always visible when enabled) */}
          {canUpload && (
            <IconButton
              onClick={handleUploadClick}
              disabled={!canUpload || uploading || isGenerating}
              title={uploadTitle}
              size="small"
            >
              {uploading ? (
                <CircularProgress size={20} />
              ) : (
                <Upload size={20} />
              )}
            </IconButton>
          )}

          {/* Delete Icon (always visible when there's a file, placed last) */}
          {canDelete && (
            <IconButton
              onClick={handleDelete}
              disabled={deleting || uploading || isGenerating}
              title="Delete image"
              size="small"
              color="error"
            >
              {deleting ? <CircularProgress size={20} /> : <Trash2 size={20} />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Helper Text */}
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}

      {/* Error Message */}
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {error}
        </Typography>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};
